import React, { useState, useEffect } from 'react';
import { Bucket } from '../types';
import { apiService } from '../services/api';
import { getCurrentUserType } from '../amplify-config';
import EmptyBucketConfirmModal from './EmptyBucketConfirmModal';
import DeleteCrateConfirmModal from './DeleteCrateConfirmModal';
import { useUploadContext } from '../context/UploadContext';

interface BucketListProps {
  onSelectBucket: (bucket: Bucket) => void;
  selectedBucket: Bucket | null;
  refreshTrigger: number;
  onBucketDeleted?: () => void;
  onStatsUpdate?: (count: number, totalSize: number, quota?: { usedGB: number; totalGB: number; percentUsed: number }) => void;
  onBucketsLoaded?: (buckets: Bucket[]) => void;
}


const BucketList: React.FC<BucketListProps> = ({ onSelectBucket, selectedBucket, refreshTrigger, onBucketDeleted, onStatsUpdate, onBucketsLoaded }) => {
  const userType = getCurrentUserType();
  const { batches } = useUploadContext();
  const [buckets, setBuckets] = useState<Bucket[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [deletingBucket, setDeletingBucket] = useState<string | null>(null);
  const [emptyingBucket, setEmptyingBucket] = useState<string | null>(null);
  const [emptyConfirmModal, setEmptyConfirmModal] = useState<{
    isOpen: boolean;
    bucketName: string;
    bucketDisplayName: string;
  }>({
    isOpen: false,
    bucketName: '',
    bucketDisplayName: '',
  });
  const [deleteConfirmModal, setDeleteConfirmModal] = useState<{
    isOpen: boolean;
    bucketName: string;
    bucketDisplayName: string;
  }>({
    isOpen: false,
    bucketName: '',
    bucketDisplayName: '',
  });

  useEffect(() => {
    fetchBuckets();
  }, [refreshTrigger]);

  const formatBucketName = (bucket: Bucket): string => {
    // Use displayName from backend if available (for Lyve buckets)
    if (bucket.displayName) {
      return bucket.displayName.length > 30 
        ? bucket.displayName.substring(0, 30) + '...' 
        : bucket.displayName;
    }
    
    const fullName = bucket.name;
    
    // Remove provider-specific prefixes
    // AWS S3: smart-storage-{userId}-{userProvidedName}
    // OVH new: ovh-{regionCode}-{userId}-{userProvidedName}
    // OVH old: ovh-{userId}-{userProvidedName}

    // Format: crate-{uid8}-{region}-{name} — skip first three segments
    if (fullName.startsWith('crate-')) {
      const withoutPrefix = fullName.slice(6); // strip 'crate-'
      const secondDash = withoutPrefix.indexOf('-', 9); // skip uid8 (8 chars + dash)
      if (secondDash !== -1) {
        const userProvidedName = withoutPrefix.slice(secondDash + 1);
        return userProvidedName.length > 30
          ? userProvidedName.substring(0, 30) + '...'
          : userProvidedName;
      }
    }

    return fullName.length > 30 ? fullName.substring(0, 30) + '...' : fullName;
  };
  
  const getProviderBadge = (provider?: string) => {
    const providerType = provider || 'S3';

    // Personal users see OVH as "DC4S"
    if (providerType === 'OVH' && userType === 'personal') {
      return (
        <span style={{
          display: 'inline-block',
          padding: '0.25rem 0.5rem',
          backgroundColor: '#4caf50',
          color: 'white',
          borderRadius: '4px',
          fontSize: '0.75rem',
          fontWeight: 600,
          marginRight: '0.5rem'
        }}>
          DC4S
        </span>
      );
    } else if (providerType === 'OVH') {
      return (
        <span style={{
          display: 'inline-block',
          padding: '0.25rem 0.5rem',
          backgroundColor: '#43a047',
          color: 'white',
          borderRadius: '4px',
          fontSize: '0.75rem',
          fontWeight: 600,
          marginRight: '0.5rem'
        }}>
          OVH
        </span>
      );
    } else {
      return (
        <span style={{
          display: 'inline-block',
          padding: '0.25rem 0.5rem',
          backgroundColor: '#2196f3',
          color: 'white',
          borderRadius: '4px',
          fontSize: '0.75rem',
          fontWeight: 600,
          marginRight: '0.5rem'
        }}>
          S3
        </span>
      );
    }
  };

  const formatSize = (bytes: number): string => {
    const gb = bytes / (1024 * 1024 * 1024);
    if (gb < 0.01) {
      const mb = bytes / (1024 * 1024);
      return mb < 0.01 ? `${(bytes / 1024).toFixed(2)} KB` : `${mb.toFixed(2)} MB`;
    }
    return `${gb.toFixed(2)} GB`;
  };

  const formatRegion = (region: string): string => {
    const regionMap: { [key: string]: string } = {
      'us-east-1': 'US East (N. Virginia)',
      'us-east-2': 'US East (Ohio)',
      'us-west-1': 'US West (N. California)',
      'us-west-2': 'US West (Oregon)',
      'eu-west-1': 'Europe (Ireland)',
      'eu-west-2': 'Europe (London)',
      'eu-west-3': 'Europe (Paris)',
      'eu-central-1': 'Europe (Frankfurt)',
      'eu-north-1': 'Europe (Stockholm)',
      'ap-south-1': 'Asia Pacific (Mumbai)',
      'ap-northeast-1': 'Asia Pacific (Tokyo)',
      'ap-northeast-2': 'Asia Pacific (Seoul)',
      'ap-southeast-1': 'Asia Pacific (Singapore)',
      'ap-southeast-2': 'Asia Pacific (Sydney)',
      'ca-central-1': 'Canada (Central)',
      'sa-east-1': 'South America (São Paulo)',
      // OVH region codes
      'cph': 'Northern Europe (Copenhagen)',
      'mil': 'Southern Europe (Milan)',
      'de':  'Central Europe (Germany)',
      'bhs': 'United States (Beauharnois)',
      'par': 'Western Europe (Paris)',
      'uk':  'United Kingdom',
      'sgp': 'Asia Pacific (Singapore)',
      'rba': 'Africa (Rabat)',
      'syd': 'Australia (Sydney)',
      'tor': 'Canada (Toronto)',
    };
    return regionMap[region] || region;
  };

  const calculateTotalSize = (bucketsList: Bucket[]): number => {
    console.log('Calculating total size for buckets:', bucketsList);
    const total = bucketsList.reduce((total, bucket) => {
      console.log(`Bucket ${bucket.name}: totalSize = ${bucket.totalSize}`);
      return total + (bucket.totalSize || 0);
    }, 0);
    console.log('Final total size:', total);
    return total;
  };

  const fetchBuckets = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await apiService.listBuckets();
      console.log('Buckets response:', response);
      if (response.success) {
        console.log('Buckets data:', response.buckets);
        setBuckets(response.buckets);
        onBucketsLoaded?.(response.buckets);
        
        // Update stats with quota information
        if (onStatsUpdate) {
          const totalSize = calculateTotalSize(response.buckets);
          console.log('Total size calculated:', totalSize);
          
          // Pass quota information if available
          const quota = (response as any).quota;
          if (quota) {
            onStatsUpdate(response.buckets.length, totalSize, {
              usedGB: quota.usedGB,
              totalGB: quota.totalGB,
              percentUsed: quota.percentUsed
            });
          } else {
            onStatsUpdate(response.buckets.length, totalSize);
          }
        }
      } else {
        setError('Failed to load buckets');
      }
    } catch (err: any) {
      console.error('Failed to fetch buckets:', err);
      setError('Failed to load data crates');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBucket = async (bucketName: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent bucket selection when clicking delete
    
    // Find the bucket to get its display name
    const bucket = buckets.find(b => b.name === bucketName);
    const displayName = bucket?.displayName || formatBucketName(bucket || { name: bucketName } as Bucket);
    
    // Show confirmation modal
    setDeleteConfirmModal({
      isOpen: true,
      bucketName,
      bucketDisplayName: displayName
    });
  };

  const handleConfirmDelete = async () => {
    const bucketName = deleteConfirmModal.bucketName;
    
    setDeleteConfirmModal({ isOpen: false, bucketName: '', bucketDisplayName: '' });
    setDeletingBucket(bucketName);
    setError('');

    try {
      const response = await apiService.deleteBucket(bucketName);
      if (response.success) {
        // Remove bucket from local state
        const updatedBuckets = buckets.filter(b => b.name !== bucketName);
        setBuckets(updatedBuckets);
        
        // Update stats immediately
        if (onStatsUpdate) {
          const totalSize = calculateTotalSize(updatedBuckets);
          // Recalculate quota after deletion
          const response = await apiService.listBuckets();
          const quota = (response as any).quota;
          if (quota) {
            onStatsUpdate(updatedBuckets.length, totalSize, {
              usedGB: quota.usedGB,
              totalGB: quota.totalGB,
              percentUsed: quota.percentUsed
            });
          } else {
            onStatsUpdate(updatedBuckets.length, totalSize);
          }
        }
        
        // If deleted bucket was selected, clear selection
        if (selectedBucket?.name === bucketName) {
          onSelectBucket(null as any);
        }
        
        // Notify parent component
        if (onBucketDeleted) {
          onBucketDeleted();
        }
      } else {
        setError(response.message || 'Failed to delete data crate');
      }
    } catch (err: any) {
      console.error('Failed to delete bucket:', err);
      setError(err.message || 'Failed to delete data crate');
    } finally {
      setDeletingBucket(null);
    }
  };

  const handleEmptyBucket = async (bucket: Bucket, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent bucket selection when clicking empty
    
    // Show confirmation modal
    setEmptyConfirmModal({
      isOpen: true,
      bucketName: bucket.name,
      bucketDisplayName: formatBucketName(bucket),
    });
  };

  const performEmptyBucket = async () => {
    const bucketName = emptyConfirmModal.bucketName;
    setEmptyConfirmModal({ isOpen: false, bucketName: '', bucketDisplayName: '' });
    setEmptyingBucket(bucketName);
    setError('');

    try {
      const response = await apiService.emptyBucket(bucketName);
      if (response.success) {
        // Check if crate was already empty
        if (response.deletedCount === 0) {
          alert('Crate is already empty.');
        } else {
          alert(`Successfully emptied crate. Deleted ${response.deletedCount} objects/versions.`);
        }
        
        // Notify parent to refresh object list if this bucket is selected
        if (onBucketDeleted) {
          onBucketDeleted();
        }
      } else {
        setError(response.message || 'Failed to empty data crate');
      }
    } catch (err: any) {
      console.error('Failed to empty bucket:', err);
      setError(err.message || 'Failed to empty data crate');
    } finally {
      setEmptyingBucket(null);
    }
  };

  return (
    <div className="bucket-list-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h2 className="bucket-list-title" style={{ margin: 0 }}>My Data Crates</h2>
        <button
          onClick={fetchBuckets}
          disabled={loading}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: loading ? '#e0e0e0' : '#667eea',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: loading ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            fontSize: '0.875rem',
            fontWeight: 500,
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            if (!loading) {
              e.currentTarget.style.backgroundColor = '#5568d3';
            }
          }}
          onMouseLeave={(e) => {
            if (!loading) {
              e.currentTarget.style.backgroundColor = '#667eea';
            }
          }}
          title="Refresh bucket list"
        >
          <span style={{ 
            display: 'inline-block',
            animation: loading ? 'spin 1s linear infinite' : 'none'
          }}>
            🔄
          </span>
          Refresh
        </button>
      </div>
      
      {loading && <p>Loading data crates...</p>}
      {error && <div className="form-error">{error}</div>}
      
      {!loading && buckets.length === 0 && (
        <p style={{ color: '#999', textAlign: 'center' }}>
          No data crates yet. Create your first data crate below.
        </p>
      )}

      {buckets.map((bucket) => (
        <div
          key={bucket.name}
          className={`bucket-item ${selectedBucket?.name === bucket.name ? 'selected' : ''}`}
          onClick={() => onSelectBucket(bucket)}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {/* First row: Badge + Name */}
            <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
              {getProviderBadge(bucket.provider)}
              {formatBucketName(bucket)}
            </div>
            {/* Second row: Buttons */}
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              {(() => {
                const uploadActive = batches.some(b => b.bucketName === bucket.name && b.isUploading);
                const emptyDisabled = emptyingBucket === bucket.name || deletingBucket === bucket.name || uploadActive;
                return (
                  <button
                    onClick={(e) => handleEmptyBucket(bucket, e)}
                    disabled={emptyDisabled}
                    style={{
                      padding: '0.5rem 1rem',
                      backgroundColor: '#ffc107',
                      color: '#000',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: emptyDisabled ? 'not-allowed' : 'pointer',
                      fontSize: '0.875rem',
                      opacity: emptyDisabled ? 0.6 : 1
                    }}
                    title={uploadActive ? 'Upload in progress — wait for it to finish before emptying' : 'Delete all objects and versions from this crate'}
                  >
                    {emptyingBucket === bucket.name ? 'Emptying...' : 'Empty'}
                  </button>
                );
              })()}
              <button
                onClick={(e) => handleDeleteBucket(bucket.name, e)}
                disabled={deletingBucket === bucket.name || emptyingBucket === bucket.name}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: (deletingBucket === bucket.name || emptyingBucket === bucket.name) ? 'not-allowed' : 'pointer',
                  fontSize: '0.875rem',
                  opacity: (deletingBucket === bucket.name || emptyingBucket === bucket.name) ? 0.6 : 1
                }}
                title="Delete this empty crate"
              >
                {deletingBucket === bucket.name ? 'Deleting...' : 'Delete'}
              </button>
            </div>
            {/* Third row: Metadata */}
            <div style={{ fontSize: '0.875rem', color: '#666' }}>
              {formatRegion(bucket.region || 'Unknown')} | {formatSize(bucket.totalSize || 0)} | Created: {new Date(bucket.creationDate).toLocaleDateString()}
            </div>
          </div>
        </div>
      ))}
      
      <EmptyBucketConfirmModal
        isOpen={emptyConfirmModal.isOpen}
        bucketDisplayName={emptyConfirmModal.bucketDisplayName}
        onConfirm={performEmptyBucket}
        onCancel={() => setEmptyConfirmModal({ isOpen: false, bucketName: '', bucketDisplayName: '' })}
      />
      
      <DeleteCrateConfirmModal
        isOpen={deleteConfirmModal.isOpen}
        crateName={deleteConfirmModal.bucketDisplayName}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteConfirmModal({ isOpen: false, bucketName: '', bucketDisplayName: '' })}
      />
    </div>
  );
};

export default BucketList;
